'use client'

import { cn } from '@morpho-org/ui-v6/utils'
import { type CSSProperties, memo, type PropsWithChildren, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

export interface FixedToBodyProps extends PropsWithChildren {
  /** Distance from the top of the viewport, in pixels */
  top?: number
  /** Distance from the left of the viewport, in pixels */
  left?: number
  /** Distance from the right of the viewport, in pixels */
  right?: number
  /** Distance from the bottom of the viewport, in pixels */
  bottom?: number
  /** Id of the element to portal into (defaults to document.body) */
  portalContainerId?: string
  /** Additional class names */
  className?: string
  /** Inline styles */
  style?: CSSProperties
}

/**
 * FixedToBody component - Tailwind replica of dapp-ui-v5 FixedToBody
 *
 * Portals its children into document.body (or the element matching
 * `portalContainerId`) inside a `position: fixed` wrapper.
 *
 * Matches v5 FixedRoot:
 * - position: fixed
 * - top/left/right/bottom applied only when defined (px values via inline style)
 * - z-index: z-40 (app frame chrome, below overlays)
 *
 * @example
 * ```tsx
 * <FixedToBody top={0} right={0}>
 *   <Banner />
 * </FixedToBody>
 * ```
 */
const FixedToBody = ({
  children,
  portalContainerId,
  className,
  style,
  top,
  left,
  right,
  bottom
}: FixedToBodyProps) => {
  const [container, setContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (typeof document === 'undefined') return
    const target = portalContainerId ? document.getElementById(portalContainerId) : document.body
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setContainer(target ?? null)
  }, [portalContainerId])

  if (!container) return null

  return createPortal(
    <div className={cn('z-40 fixed', className)} style={{ top, left, right, bottom, ...style }}>
      {children}
    </div>,
    container
  )
}

const MemoizedFixedToBody = memo(FixedToBody)
MemoizedFixedToBody.displayName = 'FixedToBody'

export { MemoizedFixedToBody as FixedToBody }
